import { Controller } from "@hotwired/stimulus";
import { visit } from "@hotwired/turbo";

// Is el a form field whose value the user types? Checkboxes and radios carry
// no draft, and a morph syncing their checked state is wanted.
function isTypedField(el) {
  if (el instanceof HTMLTextAreaElement) return true;
  if (el instanceof HTMLInputElement) {
    return !["checkbox", "radio", "button", "submit", "reset", "file", "image", "hidden"].includes(el.type);
  }
  return false;
}

// Periodically re-fetches the current URL through Turbo so the page
// content updates in place. Pair with `<meta name="turbo-refresh-method"
// content="morph">` (in the page's head_extra block) to keep scroll
// position, focus, and form state across refreshes.
//
// Usage:
//   <div data-controller="auto-refresh"
//        data-auto-refresh-interval-ms-value="10000"></div>
//
// 0 / negative interval disables the timer.
export default class extends Controller {
  static values = { intervalMs: { type: Number, default: 0 } };

  connect() {
    if (this.intervalMsValue <= 0) return;
    this.timer = setInterval(() => this.refresh(), this.intervalMsValue);
    // Turbo's morph overwrites a typed field with the server's (empty) value
    // unless ignoreActiveValue is set, and Turbo does not set it. Cancelling
    // the value morph keeps drafts alive through refreshes, focused or not.
    this.onBeforeMorphAttribute = (e) => {
      if (e.detail?.attributeName !== "value") return;
      if (isTypedField(e.target)) e.preventDefault();
    };
    document.addEventListener("turbo:before-morph-attribute", this.onBeforeMorphAttribute);
  }

  disconnect() {
    if (this.timer) clearInterval(this.timer);
    if (this.onBeforeMorphAttribute) {
      document.removeEventListener("turbo:before-morph-attribute", this.onBeforeMorphAttribute);
    }
  }

  refresh() {
    // A refresh morphs the body, which moves focus off a field the user is
    // typing in (IME state, caret). Wait for the next tick instead — the
    // draft is preserved by the morph guard above once focus leaves.
    if (isTypedField(document.activeElement)) return;
    visit(window.location.href, { action: "replace" });
  }
}
